import type { ReactNode } from 'react';
import type { ReportRead } from 'definitions/report-read';
import type { useExperimentReport } from '@/state/experiments/useExperimentReport';
import { REPORT_TEXT_LIMITS } from 'definitions';
import { buildReportSummary } from '@/state/experiments/report-summary';
import {
  EXPECTATION_CHOICES,
  NEXT_STEP_CHOICES,
  OUTSIDE_FACTOR_CHOICES,
  completeness,
} from '@/state/experiments/report-questions';
import { ReportCard } from './ReportCard';
import { MultiChoice, Question, SingleChoice, TextAnswer } from './ReportChips';

export type ReportManager = ReturnType<typeof useExperimentReport>;

/** What the card says about the experiment, none of it an answer. */
export type ReportContext = {
  experimentId: number;
  experimentName: string;
  hypothesis: string;
  startsAt: number | null;
  endsAt: number | null;
  /** The primary metric's display name, e.g. 'Purchase' or 'Conversions'. */
  metric: string;
  /** The device tag: 'mobile', 'all devices'. */
  devices: string;
  /** The website's host, for the note under the card; empty when unknown. */
  website: string;
};

type SummaryFor = { manager: ReportManager; context: ReportContext; read: ReportRead | null };

/** The card's text for the answers as they are now, so it follows every edit. */
export const summaryFor = ({ manager, context, read }: SummaryFor) =>
  buildReportSummary({
    draft: manager.draft,
    experiment: {
      id: context.experimentId,
      name: context.experimentName,
      hypothesis: context.hypothesis,
      startsAt: context.startsAt,
      endsAt: context.endsAt,
      metric: context.metric,
      devices: context.devices,
    },
    read,
    author: manager.author,
    savedAt: manager.savedAt,
  });

type QuestionsProps = { manager: ReportManager };

/** The three chip questions, in the order the card reads. */
export const ChipQuestions = ({ manager }: QuestionsProps) => {
  const { draft, setAnswer, toggleFactor } = manager;

  return (
    <>
      <Question title="Did the result match your expectation?">
        <SingleChoice
          label="Did the result match your expectation?"
          choices={EXPECTATION_CHOICES}
          value={draft.expectation}
          onChange={(value) => setAnswer('expectation', value)}
        />
      </Question>

      <Question title="What are you doing next?">
        <SingleChoice
          label="What are you doing next?"
          choices={NEXT_STEP_CHOICES}
          value={draft.nextStep}
          onChange={(value) => setAnswer('nextStep', value)}
        />
      </Question>

      <Question title="Anything outside the test that may have affected it?">
        <MultiChoice
          label="Outside factors"
          choices={OUTSIDE_FACTOR_CHOICES}
          values={draft.outsideFactors}
          onToggle={toggleFactor}
        />
        {draft.outsideFactors.includes('other') && (
          <TextAnswer
            label="What else may have affected it"
            value={draft.outsideNote}
            onChange={(value) => setAnswer('outsideNote', value)}
            limit={REPORT_TEXT_LIMITS.outsideNote}
            placeholder="What else was going on? (optional)"
            rows={2}
          />
        )}
      </Question>
    </>
  );
};

type TextQuestionsProps = QuestionsProps & {
  /** Which text question comes first. The review screen follows the card (why, then lesson); the drawer leads with the lesson. */
  first: 'explanation' | 'learning';
};

export const TextQuestions = ({ manager, first }: TextQuestionsProps) => {
  const { draft, setAnswer } = manager;

  const explanation = (
    <Question title="What do you think explains it?">
      <TextAnswer
        label="What do you think explains it?"
        value={draft.explanation}
        onChange={(value) => setAnswer('explanation', value)}
        limit={REPORT_TEXT_LIMITS.explanation}
        placeholder="Your best explanation for the result (optional)"
      />
    </Question>
  );

  const learning = (
    <Question title="What did you learn that someone else should know?">
      <TextAnswer
        label="What did you learn that someone else should know?"
        value={draft.learning}
        onChange={(value) => setAnswer('learning', value)}
        limit={REPORT_TEXT_LIMITS.learning}
        placeholder="What would you tell a colleague running a similar test? (optional)"
      />
    </Question>
  );

  return first === 'learning' ? (
    <>
      {learning}
      {explanation}
    </>
  ) : (
    <>
      {explanation}
      {learning}
    </>
  );
};

type ReportFormProps = {
  manager: ReportManager;
  context: ReportContext;
  read: ReportRead | null;
  /** Above the questions: the modal's title. */
  heading?: ReactNode;
  /** Below the questions: the modal's buttons. */
  footer?: ReactNode;
};

/**
 * The review screen of the end-of-test modal: every question on the left, and the finished
 * card they build on the right, so what someone checks before saving is exactly what the Debrief
 * tab shows afterwards. The modal's column is narrow, so the card stacks facts above answers.
 */
export const ReportForm = ({ manager, context, read, heading, footer }: ReportFormProps) => {
  const summary = summaryFor({ manager, context, read });
  const progress = completeness(manager.draft);
  const note = `Saved with this experiment${context.website ? ` on ${context.website}` : ''}, for your team to read later.`;

  return (
    <div className="grid md:grid-cols-[minmax(0,1fr)_360px]">
      <div className="flex flex-col gap-[18px] p-5">
        {heading}
        <ChipQuestions manager={manager} />
        <TextQuestions manager={manager} first="explanation" />
        {footer}
      </div>

      <div className="bg-panel2 border-line flex flex-col gap-3 border-t md:border-t-0 md:border-l">
        <ReportCard summary={summary} />

        <div className="flex flex-col gap-3 px-6 pb-6">
          {progress.answered < progress.total && (
            <div className="flex flex-col gap-1.5">
              <div className="bg-track h-1 rounded-pill">
                <div
                  className="bg-ok h-1 rounded-pill transition-all duration-150"
                  style={{ width: `${(progress.answered / progress.total) * 100}%` }}
                />
              </div>
              <span className="text-dim font-mono text-[10px]">
                {progress.answered} of {progress.total} answered
              </span>
            </div>
          )}
          <p className="supporting">{note}</p>
        </div>
      </div>
    </div>
  );
};
